import { useMemo } from "react";
import {DataRow, DenominatorType, UnitType} from "./types";

interface CompoundMap {
    [compound: string]: { [key: string]: number };
}

interface CompoundOccurrences {
    [compound: string]: string[];
}

interface ProductTotals {
    [key: string]: number;
}

interface Dataset {
    label: string;
    data: number[];
    backgroundColor?: string;
    errorBars?: { [key: string]: { plus: number; minus: number } };
    extras?: { [key: string]: { originalValue: any; stdev: number | null; unit: string } };
}

export const defaultGenerateColors = (count: number) => {
    const colors = [];
    for (let i = 0; i < count; i++) {
        const hue = (i * 137.5) % 360;
        const color = `hsl(${hue}, 70%, 50%)`;
        colors.push(color);
    }
    return colors;
};

export const defaultBuildLabels = (data: any[]): string[] => {
    return data.map((row) => `${row.NAME}`);
};

export const transformData = (
    items: any[],
    selectedKeys: string[],
    mode: 'direct' | 'percentage' | 'normalized' | 'logarithmic',
    getKey: (item: any) => string,
    getValue: (row: any) => number,
    getCompound: (row: any) => string,
    getStdev: (row: any) => number | null,
    getUnit: (item: any) => string,
    compoundFilter: 'all' | 'inCommon' | 'notInCommon',
    includeNonNumeric: boolean,
    allowedInchiKeys: string[] = []
): { labels: string[]; datasets: Dataset[] } => {
    const compoundsMap: CompoundMap = {};
    const compoundOccurrences: CompoundOccurrences = {};
    const productTotals: ProductTotals = {};
    const stdevMap: { [compound: string]: { [key: string]: number | null } } = {};
    const originalValuesMap: { [compound: string]: { [key: string]: any } } = {};
    const unitMap: { [compound: string]: { [key: string]: string } } = {};

    items.forEach(item => {
        const key = getKey(item);
        if (selectedKeys.length === 0 || selectedKeys.includes(key)) {
            let totalForProduct = 0;
            item.DATA.forEach((row: any) => {
                const compound = getCompound(row) || 'Unknown';
                const inchiKey = row.INCHIKEY || '';
                // Skip if INCHIKEY filter is applied and this INCHIKEY isn't allowed
                if (allowedInchiKeys.length > 0 && !allowedInchiKeys.includes(inchiKey)) {
                    return;
                }
                // Use CONCENTRATION or AVERAGE based on data structure
                const rawValue = row.CONCENTRATION !== undefined ? row.CONCENTRATION : row.AVERAGE;
                const numericValue = Number(rawValue);
                const isNumericAverage = !isNaN(numericValue) && rawValue !== null;
                const lodValue = Number(row.LOD);
                const hasValidLod = !isNaN(lodValue);
                const loqValue = Number(row.LOQ);
                const hasValidLoq = !isNaN(loqValue);
                let value: number | null = null;

                if (isNumericAverage) {
                    value = numericValue;
                } else if (includeNonNumeric && typeof rawValue === 'string') {
                    if (rawValue.includes('D') && hasValidLod) {
                        value = lodValue;
                    } else if (rawValue.includes('Q') && hasValidLoq) {
                        value = loqValue;
                    }
                }

                // Include compound only if it has a valid numeric value and passes includeNonNumeric filter
                if (value !== null && value > 0 && compound !== 'Unknown' && (isNumericAverage || includeNonNumeric)) {
                    if (!compoundsMap[compound]) {
                        compoundsMap[compound] = {};
                        compoundOccurrences[compound] = [];
                        stdevMap[compound] = {};
                        originalValuesMap[compound] = {};
                        unitMap[compound] = {};
                    }
                    compoundsMap[compound][key] = value;
                    totalForProduct += value;
                    stdevMap[compound][key] = getStdev(row);
                    originalValuesMap[compound][key] = rawValue ?? 'N/A';
                    unitMap[compound][key] = row.UNIT || getUnit(item);
                    if (!compoundOccurrences[compound].includes(key)) {
                        compoundOccurrences[compound].push(key);
                    }
                }
            });
            productTotals[key] = totalForProduct;
        }
    });

    const allCompounds = Object.keys(compoundsMap);
    const compoundsToShow = selectedKeys.length > 1
        ? (compoundFilter === 'inCommon'
            ? allCompounds.filter(compound => compoundOccurrences[compound].length === selectedKeys.length)
            : compoundFilter === 'notInCommon'
                ? allCompounds.filter(compound => compoundOccurrences[compound].length < selectedKeys.length)
                : allCompounds)
        : allCompounds;

    const datasets = items
        .filter(item => {
            const key = getKey(item);
            return selectedKeys.length === 0 || selectedKeys.includes(key);
        })
        .map((item, index) => {
            const key = getKey(item);
            const totalForProduct = productTotals[key] || 0;
            const data = compoundsToShow.map(compound => {
                const value = compoundsMap[compound]?.[key] || 0;
                switch (mode) {
                    case 'direct':
                        return value;
                    case 'percentage':
                        return totalForProduct > 0 ? (value / totalForProduct) * 100 : 0;
                    case 'normalized':
                        const maxInProduct = Math.max(...Object.values(compoundsMap).map(map => map[key] || 0));
                        return maxInProduct > 0 ? value / maxInProduct : 0;
                    case 'logarithmic':
                        const logMaxInProduct = Math.max(...Object.values(compoundsMap).map(map => map[key] || 0));
                        return logMaxInProduct > 0 ? Math.log10(value + 1) / Math.log10(logMaxInProduct + 1) * 100 : 0;
                    default:
                        return value;
                }
            });
            const errorBars = compoundsToShow.reduce((acc, compound) => {
                const stdev = stdevMap[compound]?.[key];
                if (stdev !== null && stdev !== undefined) {
                    acc[compound] = { plus: stdev, minus: stdev };
                }
                return acc;
            }, {} as { [key: string]: { plus: number; minus: number } });

            return {
                label: key,
                data,
                backgroundColor: `hsl(${index * 60}, 70%, 50%)`,
                errorBars: Object.keys(errorBars).length > 0 ? errorBars : undefined,
                extras: compoundsToShow.reduce((acc, compound) => {
                    acc[compound] = {
                        originalValue: originalValuesMap[compound]?.[key] ?? 'N/A',
                        stdev: stdevMap[compound]?.[key] ?? null,
                        unit: unitMap[compound]?.[key] ?? getUnit(item)
                    };
                    return acc;
                }, {} as { [key: string]: { originalValue: any; stdev: number | null; unit: string } })
            };
        });

    return {
        labels: compoundsToShow,
        datasets,
    };
};

// Extract unique CLASS values
export const useGetClassOptions = (filteredItems: any[]): string[] => {
    return useMemo(() => {
        const classes = new Set<string>();
        filteredItems.forEach(item => {
            item.DATA.forEach((row: any) => {
                if (row.CLASS) classes.add(row.CLASS);
            });
        });
        return ['All', ...Array.from(classes)];
    }, [filteredItems]);
};

// Filter data by selected CLASS
export const useGetFilteredItemsByClass = (filteredItems: any[], selectedClass: string) => {
    return useMemo(() => {
        if (selectedClass === 'All') return filteredItems;
        return filteredItems.map(item => ({
            ...item,
            DATA: item.DATA.filter((row: any) => row.CLASS === selectedClass),
        })).filter(item => item.DATA.length > 0);
    }, [filteredItems, selectedClass]);
};

// Unit conversion logic (numerator only)
const UNIT_CONVERSIONS: Record<UnitType, Record<UnitType, number>> = {
    'mg': { 'mg': 1, 'µg': 1000, 'microg': 1000, 'ng': 1000000 },
    'µg': { 'mg': 1/1000, 'µg': 1, 'microg': 1, 'ng': 1000 },
    'microg': { 'mg': 1/1000, 'µg': 1, 'microg': 1, 'ng': 1000 },
    'ng': { 'mg': 1/1000000, 'µg': 1/1000, 'microg': 1/1000, 'ng': 1 }
};

// Filler weights for denominator normalization (in mg)...
//@TODO: Change it from data coming from the database, and linked to analysis and product
const FILLER_WEIGHTS: Record<string, number> = {
    'Green Pre-roll Strawberry (Hemp Flower B-000510)': 400.0,
    'Green HNB v2 Strawberry (Regular) (Hemp Flower B-000510) (Sticks B-150324)': 103.0,
    'Green HNB v2 Strawberry (Regular) (Hemp Flower B-000510) (Sticks B-150324) (consecutive session #2)': 103.0,
    'Green HNB v2 Strawberry (Mentholated) (Hemp Flower B-000510) (Sticks B-150325)': 107.0,
    'Green HNB v2 Strawberry (Mentholated) (Hemp Flower B-000510) (Sticks B-150325) (consecutive session #2)': 107.0,
    'Green HNB v1 Bubblegum (Hemp Flower B-000519) (Sticks B-149025) (Abbreviatted 1st evaluation)': 100.0,
    'Grenn HNB v2 Strawberry (Hemp Flower B-000510) (Sticks B-147526) (Abbreviated 1st evaluation)': 100.0,
    'Heimat Hemp HNB (After aerosol generation)': 119.8,
    'Heimat Hemp HNB': 119.8,
    'Heimat CC Pre-Roll': 695.1,
    'Free Slim Pre-Roll': 374.2,
    'Pure Sun CBD Pre-Roll': 316.8,
    '1R6F': 625.0,
};

// Extended unit conversion including denominator
// Chart.Common.ts

export const convertUnits = (
    items: any[],
    targetNumerator: UnitType,
    targetDenominator: DenominatorType
) => {
    const getNumerator = (unit: string): UnitType => {
        switch (unit) {
            case 'µg':
            case 'microg':
                return 'µg';
            case 'mg':
                return 'mg';
            case 'ng':
                return 'ng';
            default:
                return 'mg'; // fallback
        }
    };

    const parseUnitParts = (unitStr: string): { numerator: UnitType; denominator: DenominatorType } => {
        const [numeratorRaw = 'mg', denominatorRaw = 'item'] = unitStr.split('/');
        return {
            numerator: getNumerator(numeratorRaw),
            denominator: denominatorRaw === 'mg' ? 'mg' : 'item',
        };
    };

    return items.map(item => {
        const productKey = `${item.DATA[0]?.PRODUCT || 'Unknown'}`;
        const fillerWeight = FILLER_WEIGHTS[productKey] || 1;

        return {
            ...item,
            DATA: item.DATA.map((row: DataRow) => {
                const { numerator: originalNumerator, denominator: originalDenominator } = parseUnitParts(row.UNIT || 'mg/item');

                const numeratorConversionFactor = UNIT_CONVERSIONS[originalNumerator]?.[targetNumerator] || 1;

                const denominatorConversionFactor =
                    originalDenominator === targetDenominator
                        ? 1
                        : originalDenominator === 'item' && targetDenominator === 'mg'
                            ? 1 / fillerWeight
                            : fillerWeight;

                const conversionFactor = numeratorConversionFactor * denominatorConversionFactor;

                const scaleIfNumeric = (value: any) =>
                    value != null && !isNaN(Number(value)) ? Number(value) * conversionFactor : value;

                return {
                    ...row,
                    AVERAGE: scaleIfNumeric(row.AVERAGE),
                    CONCENTRATION: scaleIfNumeric(row.CONCENTRATION),
                    STD_DEV: scaleIfNumeric(row.STD_DEV),
                    LOD: scaleIfNumeric(row.LOD),
                    LOQ: scaleIfNumeric(row.LOQ),
                    UNIT: `${targetNumerator}/${targetDenominator}`,
                };
            }),
        };
    });
};


// Tooltip callbacks to display original value (numeric, BDL, BQ) in tooltip
export const getTooltipCallbacks = (
    items: any[],
    getKey: (item: any) => string,
    getCompound: (row: any) => string,
    getValue: (row: any) => number,
    getStdev: (row: any) => number | null,
    getUnit: (item: any) => string,
    mode: 'direct' | 'percentage' | 'normalized' | 'logarithmic'
) => ({
    label: (context: any) => {
        const dataset = context.dataset;
        const index = context.dataIndex;
        const transformedValue = context.parsed.y;
        const compound = context.chart.data.labels[index];
        const key = dataset.label;
        const originalValue = dataset.extras?.[compound]?.originalValue ?? 'N/A';
        const stdev = dataset.extras?.[compound]?.stdev ?? null;
        const unit = dataset.extras?.[compound]?.unit ?? getUnit(items.find(i => getKey(i) === key)!) ?? 'Unknown';
        const stdevText = stdev !== null ? ` ±${stdev.toFixed(2)}` : '';
        const modeUnit = mode === 'percentage' ? '%' :
            mode === 'normalized' ? '%' :
                mode === 'logarithmic' ? 'Log(%)' : unit;

        return [
            `${dataset.label}`,
            `Amount: ${typeof originalValue === 'number' ? originalValue.toFixed(2) : originalValue} ${stdevText} ${unit}`,
            `${mode.charAt(0).toUpperCase() + mode.slice(1)} Value: ${transformedValue.toFixed(2)} ${modeUnit}`
        ];
    }
});
